import { isAdaAssetId } from "@sundae/cardano-utils";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { Fraction, TIntegerLike } from "@sundaeswap/fraction";

import { ADA_METADATA } from "../constants/cardano.constants";
import { FreezerItem, PoolFragmentFragment } from "../gql/generated/stats2.sdk";
import { IAssetMetaData } from "../types/Asset.types";
import {
  calculateAssetPriceInADA,
  calculateAssetValueInUSD,
  getAssetName,
  isSameAsset,
} from "./assets.utils";
import { parseScientific, stringToBigint } from "./number-format";
import { getUnlockedTokensPoolShare } from "./positions.utils";

/**
 * The key is an AssetID for the pools LP token
 */
export type TAssetIdPoolMap = Map<string, PoolFragmentFragment>;

/**
 * Calculates and returns the price ratio of two assets in a pool.
 *
 * The function expects an object with properties `assetAId`, `assetAmountA`, and `assetAmountB`.
 * - `assetAId` should be a string representing the ID of the first asset.
 * - `assetAmountA` and `assetAmountB` are instances of `AssetAmount` representing the amount of each asset in the pool.
 *
 * The price ratio is calculated as the value of `assetAmountA` divided by `assetAmountB` if the `assetAId` is equivalent to 'Ada',
 * otherwise it's calculated as the value of `assetAmountB` divided by `assetAmountA`.
 *
 * The function returns an instance of `AssetAmount` containing the calculated price ratio.
 *
 * @param {Object} params - An object containing `assetAId`, `assetAmountA`, and `assetAmountB`.
 * @param {string} params.assetAId - The ID of the first asset.
 * @param {AssetAmount} params.assetAmountA - The amount of the first asset in the pool.
 * @param {AssetAmount} params.assetAmountB - The amount of the second asset in the pool.
 *
 * @returns {AssetAmount} The price ratio of the two assets in the pool.
 */
export const getPoolDisplayPrice = ({
  assetAId,
  assetAmountA,
  assetAmountB,
}: {
  assetAId: string;
  assetAmountA: AssetAmount;
  assetAmountB: AssetAmount;
}) => {
  const price = isAdaAssetId(assetAId)
    ? AssetAmount.fromValue(assetAmountA.value.div(assetAmountB.value), assetAmountA.decimals)
    : AssetAmount.fromValue(assetAmountB.value.div(assetAmountA.value), assetAmountA.decimals);
  return price;
};

/**
 * Generates a name for a pool consisting of two assets.
 *
 * The function expects an object with properties `assetA` and `assetB`.
 * - `assetA` and `assetB` are instances of `IAssetMetaData` representing the metadata of each asset.
 *
 * The pool name is generated by concatenating the names of `assetA` and `assetB`, separated by a hyphen.
 * The name of each asset is retrieved using the `getAssetName` function.
 *
 * @param {Object} params - An object containing `assetA` and `assetB`.
 * @param {IAssetMetaData} params.assetA - The metadata of the first asset.
 * @param {IAssetMetaData} params.assetB - The metadata of the second asset.
 *
 * @returns {string} The generated pool name.
 */
export const getPoolName = ({
  assetA,
  assetB,
}: {
  assetA: IAssetMetaData;
  assetB: IAssetMetaData;
}) => {
  const assetAName = getAssetName(assetA);
  const assetBName = getAssetName(assetB);
  return `${assetAName}-${assetBName}`;
};

/**
 * Generates a pool pair ID from an array of asset IDs.
 *
 * The function expects an array of strings representing asset IDs.
 *
 * The pool pair ID is generated by first sorting the asset IDs lexicographically, and then
 * joining them into a single string separated by an underscore.
 *
 * @param {string[]} assetIds - An array of asset IDs.
 *
 * @returns {string} The generated pool pair ID.
 */
export const getPoolPairId = (assetIds: string[]) => assetIds.sort().join("_");

/**
 * Grabs the asset ids within the pool as an array, sorted by direction.
 *
 * @param {PoolFragmentFragment} pool The pool fragment data.
 * @returns {[string, string]} The asset IDs.
 */
export const getPoolPairAssetIds = (pool: PoolFragmentFragment): [string, string] => [
  pool.assetA.assetId,
  pool.assetB.assetId,
];

/**
 * Checks if a given asset is a liquidity pool (LP) asset based on its `policyId` and `assetName`.
 *
 * The function expects an object with properties `policyId` and `assetName` from the `Asset` type.
 *
 * The check is performed by comparing the `policyId` with a predefined LP asset policy ID and
 * checking if the `assetName` starts with a predefined LP asset name prefix. Both predefined values
 * are retrieved from `window.__APP_CONFIG.SundaeSwap`.
 *
 * If the `lpAssetPolicyId` is not defined, the function will only check if the `assetName` starts with
 * the `lpAssetNamePrefix`. If the `lpAssetPolicyId` is defined, the `policyId` must match it and the
 * `assetName` must start with the `lpAssetNamePrefix` for the function to return true.
 *
 * @param {string} assetID The PolicyID and AssetName separated by a decimal.
 *
 * @returns {boolean} `true` if the asset is an LP asset, `false` otherwise.
 */
export const isLPAsset = (assetID: string) => {
  const policyId = assetID.split(".")[0];
  const assetName = assetID.split(".")[1];
  const { lpAssetPolicyId, lpAssetNamePrefix } = window.__APP_CONFIG.SundaeSwap;
  return (
    (!lpAssetPolicyId || policyId === lpAssetPolicyId) && assetName?.startsWith(lpAssetNamePrefix)
  );
};

/**
 * Filters pools that have both assets with a balance.
 *
 * The function expects an array of assets and an array of pools. Each asset in the assets array
 * is an instance of `IMappedAsset<IAssetMetaData>`. Each pool in the pools array is a `PoolFragmentFragment`.
 *
 * The function filters the pools array based on whether both assets of each pool have a balance.
 * The balance is determined by checking if the assets array contains an asset with the same metadata
 * as each asset of the pool using the `isSameAsset` function.
 *
 * @param {IMappedAsset<IAssetMetaData>[]} assets - An array of assets with a balance.
 * @param {PoolFragmentFragment[]} pools - An array of pools to be filtered.
 *
 * @returns {PoolFragmentFragment[]} An array of pools that have both assets with a balance.
 */
export const getPoolsWithBalance = (
  assets: IMappedAsset<IAssetMetaData>[],
  pools: PoolFragmentFragment[]
) => {
  return pools.filter(({ assetA, assetB }) => {
    const hasBalanceAssetA = Boolean(assets.find(({ metadata }) => isSameAsset(metadata, assetA)));
    const hasBalanceAssetB = Boolean(assets.find(({ metadata }) => isSameAsset(metadata, assetB)));

    return hasBalanceAssetA && hasBalanceAssetB;
  });
};

/**
 * Calculates and returns the ratio of two assets in a pool.
 *
 * The function expects a `PoolFragmentFragment` object representing a pool, which should include
 * `quantityA`, `quantityB`, `assetA`, and `assetB` properties.
 *
 * The ratio is calculated as the quantity of `assetA` divided by the quantity of `assetB` if the
 * `assetId` of `assetA` is equivalent to 'Ada', otherwise it's calculated as the quantity of
 * `assetB` divided by the quantity of `assetA`.
 *
 * @param {PoolFragmentFragment} pool - The pool to calculate the ratio for.
 *
 * @returns {number} The ratio of the two assets in the pool.
 */
export const calculatePoolRatio = (pool: PoolFragmentFragment) => {
  const assetAmountA = new AssetAmount(pool.quantityA ?? 0n, pool.assetA.decimals);
  const assetAmountB = new AssetAmount(pool.quantityB ?? 0n, pool.assetB.decimals);
  return isAdaAssetId(pool.assetA.assetId)
    ? assetAmountA.value.divide(assetAmountB.value)
    : assetAmountB.value.divide(assetAmountA.value);
};

export const calculatePercentageOfEarnedFees = (
  feesEarned: TIntegerLike | null,
  percentage: number | null
) => {
  if (!feesEarned || percentage === 0) return 0n;
  if (!percentage) return feesEarned;
  if (percentage === 1) return feesEarned;
  return stringToBigint(Fraction.asFraction(feesEarned).multiply(percentage));
};

/**
 * This function takes the active farms and returns the accumulated rewards in ADA.
 * @param {FreezerItem[]} farms - Active farms of the wallet.
 * @param {number} adaInUsd - Price of ADA in USD.
 * @returns {AssetAmount} - Accumulated rewards in ADA.
 */
export const getEarnedFarmRewardsInADA = (farms?: FreezerItem[], adaInUsd?: number) => {
  if (!farms?.length || !adaInUsd) return new AssetAmount(0n, ADA_METADATA.decimals);

  return farms.reduce((valueInADA, farm) => {
    const { rewards } = farm;

    const rewardsInAda = rewards.reduce((farmRewards, { asset, quantity }) => {
      if (asset) {
        const { assetId, priceToday } = asset;

        if (isAdaAssetId(assetId)) {
          const adaRewards = new AssetAmount(Number(quantity), ADA_METADATA.decimals);
          return farmRewards.add(adaRewards);
        }

        const assetPriceInADA = calculateAssetPriceInADA(
          Number(priceToday),
          adaInUsd,
          asset.decimals
        );

        if (!assetPriceInADA) return farmRewards;

        const assetPriceInAda = AssetAmount.fromValue(assetPriceInADA, ADA_METADATA.decimals);
        const actualQuantity = new AssetAmount(quantity, asset.decimals);
        const rewardsInAda = AssetAmount.fromValue(
          actualQuantity.value.multiply(assetPriceInAda.value).toNumber(),
          ADA_METADATA.decimals
        );

        return rewardsInAda?.add(farmRewards);
      }
      return farmRewards;
    }, new AssetAmount(0n, ADA_METADATA.decimals));

    return valueInADA.add(rewardsInAda);
  }, new AssetAmount(0n, ADA_METADATA.decimals));
};

/**
 * Calculates the TVL of a liquidity pool in ADA.
 *
 * @param {PoolFragmentFragment} pool - The liquidity pool containing information about asset quantities and prices.
 * @param {Fraction} adaInUsd - The price of ADA.
 * @returns {AssetAmount} The calculated TVL of the liquidity pool in ADA.
 */
export const calculateTVLOfPoolInADA = (pool?: PoolFragmentFragment | null, adaInUsd?: number) => {
  if (!pool || !adaInUsd) return new AssetAmount(0n, ADA_METADATA.decimals);

  const isExoticPair = !isAdaAssetId(pool.assetA.assetId) && !isAdaAssetId(pool.assetB.assetId);

  if (isExoticPair) {
    const assetAPriceInADA = calculateAssetPriceInADA(
      Number(pool.assetA.priceToday),
      adaInUsd,
      ADA_METADATA.decimals
    );
    const assetBPriceInADA = calculateAssetPriceInADA(
      Number(pool.assetB.priceToday),
      adaInUsd,
      ADA_METADATA.decimals
    );
    const assetAValueInADA = assetAPriceInADA ? Number(pool.quantityA) * assetAPriceInADA : 0;
    const assetBValueInADA = assetBPriceInADA ? Number(pool.quantityB) * assetBPriceInADA : 0;

    const tvlInADA = stringToBigint(assetAValueInADA) + stringToBigint(assetBValueInADA);

    return new AssetAmount(tvlInADA, ADA_METADATA.decimals);
  }

  const tvlInADA =
    (isAdaAssetId(pool.assetA.assetId)
      ? stringToBigint(pool.quantityA || "0")
      : stringToBigint(pool.quantityB || "0")) * 2n;

  return new AssetAmount(tvlInADA, ADA_METADATA.decimals);
};

/**
 * Calculates the 24-hour volume of a liquidity pool in ADA.
 *
 * @param {PoolFragmentFragment} pool - The liquidity pool containing information about asset quantities and fees.
 * @returns {AssetAmount} - The calculated 24-hour volume of the liquidity pool in ADA.
 *                          If pool data is not provided, returns 0 ADA.
 */
export const calculateVolume24hOfPoolInADA = (pool?: PoolFragmentFragment) => {
  if (!pool) return new AssetAmount(0n, ADA_METADATA.decimals);

  return new AssetAmount(
    stringToBigint(Number(pool.fees24H) / (Number(pool.fee) / 100)),
    ADA_METADATA.decimals
  );
};

/**
 * Calculates the relative price change of a pool's asset between today and yesterday.
 *
 * @param {string} [priceToday] - The price of the asset today, as a string (optional).
 * @param {string} [priceYesterday] - The price of the asset yesterday, as a string (optional).
 * @returns {number|null} The relative price change as a decimal (e.g., 0.05 for a 5% increase),
 *                        null if there's no price today but there was yesterday,
 *                        null if there's a price today but there wasn't yesterday,
 *                        null if there are no prices for both today and yesterday,
 *                        or null if either price is invalid.
 */
export const getPoolDeltaPriceChange = (
  priceToday?: string | null,
  priceYesterday?: string | null
) => {
  if (!priceToday || !priceYesterday) return null;

  return (
    (parseScientific(priceToday).num - parseScientific(priceYesterday).num) /
    parseScientific(priceYesterday).num
  );
};

/**
 * Gets the reserve amount of a specific asset in a given liquidity pool.
 *
 * @param {AssetAmount<IAssetMetaData>} asset - The asset for which to get the reserve.
 * @param {PoolFragmentFragment} pool - The liquidity pool from which to get the reserve.
 * @returns {bigint} The reserve amount of the asset in the pool. If the asset matches pool's assetA, it returns the quantity of assetA; otherwise, it returns the quantity of assetB.
 *
 * @example
 *
 * const assetReserve = getAssetReserve(assetAmount, liquidityPool);
 */
export const getAssetReserve = (
  asset: AssetAmount<IAssetMetaData>,
  pool: PoolFragmentFragment
): bigint => {
  return isSameAsset(asset?.metadata, pool.assetA)
    ? pool?.quantityA
      ? BigInt(pool.quantityA)
      : 0n
    : pool?.quantityB
    ? BigInt(pool.quantityB)
    : 0n;
};

/**
 * Returns a map of LP asset IDs to pools. Each entry in the map links an LP asset ID
 * to its corresponding pool. This function helps map assetIDs to their corresponding
 * pool data from a given pools object. It can be used when we have asset IDs that we want
 * to map to the corresponding pool data.
 *
 * @param {Object} pools - An object where keys are asset IDs and values are arrays of pools.
 * @param {string[]} assetIds - An array of asset IDs that we want to map to their corresponding pools.
 *
 * @returns {Map<string, PoolFragmentFragment>} - A map where keys are asset IDs and values are pools.
 */
export const getPoolMapByLPAssetId = (
  pools: PoolFragmentFragment[],
  assetIds: string[]
): TAssetIdPoolMap => {
  // Initialize an empty Map to hold the assetId to pool mapping
  const poolMap: TAssetIdPoolMap = new Map();

  pools.forEach((pool) => {
    if (assetIds.includes(pool.assetLP.assetId)) {
      poolMap.set(pool.assetLP.assetId, pool);
    }
  });

  // Return the map of asset IDs to pools
  return poolMap;
};

/**
 * Returns a map of asset IDs to pools. Each entry in the map links an asset ID
 * to its corresponding pool. This function helps map assetIDs to their corresponding
 * pool data from a given pools object. It can be used when we have asset IDs that we want
 * to map to the corresponding pool data.
 *
 * @param {Object} pools - An object where keys are asset IDs and values are arrays of pools.
 * @param {string[]} assetIds - An array of asset IDs that we want to map to their corresponding pools.
 *
 * @returns {Map<string, PoolFragmentFragment>} - A map where keys are asset IDs and values are pools.
 */
export const getPoolMapByPoolIdent = (
  pools: PoolFragmentFragment[],
  poolIdents: string[]
): TAssetIdPoolMap | undefined => {
  // If pools or assetIds is not provided, return undefined
  if (!pools || !poolIdents) return undefined;

  // Initialize an empty Map to hold the assetId to pool mapping
  const poolMap: TAssetIdPoolMap = new Map();

  pools.forEach((pool) => {
    if (poolIdents.includes(pool.ident)) {
      poolMap.set(pool.ident, pool);
    }
  });

  // Return the map of asset IDs to pools
  return poolMap;
};

/**
 * Calculates the total value of a liquidity pool token (LP) asset in both ADA and USD.
 *
 * The function calculates the share of the LP asset that the user owns in relation to the total quantity of LP assets in the pool.
 * Based on this share, it calculates the amount of both assets (assetA and assetB) in the pool that the user owns.
 * The function then calculates the value of these amounts in ADA, taking into account whether the assets are ADA or another asset.
 * The function also calculates the total value of the owned amounts of assetA and assetB in USD.
 *
 * The function requires the LP asset, the pool data, and the current price of ADA in USD as inputs.
 *
 * @param {IMappedAsset<IAssetMetaData & IAsset>} lpAsset - The LP asset that the user owns, including its quantity and metadata.
 * @param {PoolFragmentFragment} pool - The data of the pool that contains the LP asset, including the quantities and prices of assetA and assetB.
 * @param {number} adaInUsd - The current price of ADA in USD.
 *
 * @returns {Object} An object that includes the total value of the LP asset in both ADA and USD.
 * The returned object has two properties: `totalAmountInADA` and `totalAmountInUSD`.
 * `totalAmountInADA` is an AssetAmount object that represents the total value of the LP asset in ADA.
 * `totalAmountInUSD` is a number that represents the total value of the LP asset in USD.
 */
export const calculateTotalLPAssetValues = (
  lpAsset: IMappedAsset<IAssetMetaData>,
  pool: PoolFragmentFragment,
  adaInUsd: number
) => {
  const { assetA, assetB } = pool;
  const share = getUnlockedTokensPoolShare(
    lpAsset.quantity.value.toNumber(),
    Number(pool.quantityLP)
  );
  const assetAmountA = new AssetAmount(
    Math.round(Number(pool.quantityA) * share),
    pool.assetA.decimals
  );
  const assetAmountB = new AssetAmount(
    Math.round(Number(pool.quantityB) * share),
    pool.assetB.decimals
  );

  if (!share || (!assetAmountA.value.toNumber() && !assetAmountB.value.toNumber())) {
    return {
      totalAmountInADA: new AssetAmount<IAssetMetaData>(0n, ADA_METADATA),
      totalAmountInUSD: 0,
    };
  }

  const isAssetAAda = isAdaAssetId(assetA.assetId);
  const aPricedInAda = isAssetAAda
    ? adaInUsd
    : calculateAssetPriceInADA(Number(assetA.priceToday), adaInUsd, ADA_METADATA.decimals) ?? 0;
  const bPricedInAda =
    calculateAssetPriceInADA(Number(assetB.priceToday), adaInUsd, ADA_METADATA.decimals) ?? 0;

  const adaAmountA = isAssetAAda
    ? assetAmountA
    : AssetAmount.fromValue(assetAmountA.value.multiply(aPricedInAda).toNumber(), ADA_METADATA);
  const adaAmountB = AssetAmount.fromValue(
    assetAmountB.value.multiply(bPricedInAda).toNumber(),
    ADA_METADATA
  );

  const aPricedInUSD = adaAmountA.value.toNumber() * adaInUsd;
  const bPricedInUSD = adaAmountB.value.toNumber() * adaInUsd;

  return {
    totalAmountInADA: adaAmountA.add(adaAmountB),
    totalAmountInUSD: aPricedInUSD + bPricedInUSD,
  };
};

/**
 * Calculates the total value of a given asset in both ADA and USD.
 *
 * The function checks if the given asset is a liquidity pool (LP) asset. If it is, the function calls
 * `calculateTotalLPAssetValues` to calculate the total value of the LP asset in both ADA and USD.
 *
 * If the given asset is not an LP asset, the function calculates the total value of the asset in ADA,
 * taking into account whether the asset is ADA or another asset. The function then calculates the total
 * value of the asset in USD.
 *
 * The function requires the asset, the pool data (if the asset is an LP asset), and the current price
 * of ADA in USD as inputs.
 *
 * @param {IMappedAsset<IAssetMetaData>} asset - The asset that the user owns, including its quantity and metadata.
 * @param {PoolFragmentFragment} pool - The data of the pool that contains the LP asset (if the asset is an LP asset), including the quantities and prices of assetA and assetB.
 * @param {number} [adaInUsd] - The current price of ADA in USD.
 *
 * @returns {Object} An object that includes the total value of the asset in both ADA and USD.
 * The returned object has two properties: `totalAmountInADA` and `totalAmountInUSD`.
 * `totalAmountInADA` is an AssetAmount object (or the asset quantity if the asset is ADA) that represents the total value of the asset in ADA.
 * `totalAmountInUSD` is a number that represents the total value of the asset in USD.
 *
 */
export const calculateAssetValueInADAAndUSD = (
  asset?: IMappedAsset<IAssetMetaData>,
  pool?: PoolFragmentFragment,
  adaInUsd?: number
) => {
  if (!asset || !adaInUsd || !pool)
    return { totalAmountInADA: undefined, totalAmountInUSD: undefined };
  if (isLPAsset(asset.metadata.assetId) && pool) {
    return calculateTotalLPAssetValues(asset, pool, adaInUsd);
  }

  const totalAmountInADA = isAdaAssetId(asset.metadata.assetId)
    ? (asset.quantity as AssetAmount<IAssetMetaData>)
    : new AssetAmount<IAssetMetaData>(
        stringToBigint(
          asset.quantity.value
            .mul(
              calculateAssetPriceInADA(
                Number(asset.metadata.priceToday),
                adaInUsd,
                ADA_METADATA.decimals
              ) || 0
            )
            .toString(),
          ADA_METADATA.decimals
        ),
        ADA_METADATA
      );

  return {
    totalAmountInADA,
    totalAmountInUSD: calculateAssetValueInUSD(
      { metadata: asset.metadata, quantity: asset.quantity.amount },
      adaInUsd
    ),
  };
};

/**
 * Removes repeated pools from an array of pool objects.
 *
 * @param {PoolFragmentFragment[]} pools - The array of pool objects to process.
 * @returns {PoolFragmentFragment[]} - An array containing unique pool objects.
 */
export const getUniqueListOfPools = (pools: PoolFragmentFragment[]) => {
  const uniqueValues = new Set();
  const result = [];

  for (const item of pools) {
    if (!uniqueValues.has(item.ident)) {
      uniqueValues.add(item.ident);
      result.push(item);
    }
  }

  return result;
};
